<template>
  <div v-if="hasAccess">
    <PageHeader title="Préoccupations des salariés" />

    <PageContent>
      <div v-if="loading" class="concerns-state">Chargement des analyses…</div>
      <div v-else-if="error" class="concerns-state concerns-state--error">{{ error }}</div>

      <section v-else class="results-card" aria-label="Résultats de la dernière analyse">
        <header class="results-header">
          <div class="results-heading">
            <h2>Résultats de la dernière analyse</h2>
            <p v-if="latestPeriod">Période analysée : {{ latestPeriod.rangeLabel }}</p>
            <p v-else>Aucune analyse n’a encore été générée.</p>
          </div>
          <div class="results-actions">
            <button
              v-if="manualRunEnabled"
              type="button"
              class="outline-button"
              :class="{ 'outline-button--active': runPanelOpen }"
              :aria-expanded="runPanelOpen.toString()"
              @click="runPanelOpen = !runPanelOpen"
            >
              <i class="fas fa-chart-line" aria-hidden="true"></i>
              Lancer une analyse
            </button>
            <button
              type="button"
              class="outline-button outline-button--primary"
              :disabled="periods.length === 0"
              @click="openHistory()"
            >
              <i class="fas fa-history" aria-hidden="true"></i>
              Historique des analyses
            </button>
          </div>
        </header>

        <div v-if="runPanelOpen" class="run-panel">
          <label class="run-field run-field--domain">
            <span>Domaine</span>
            <select v-model="runDomainUuid">
              <option value="">Choisir un domaine</option>
              <option v-for="domain in availableDomains" :key="domain.uuid" :value="domain.uuid">
                {{ domain.label }}
              </option>
            </select>
          </label>
          <label class="run-field">
            <span>Période à analyser</span>
            <select v-model="periodType">
              <option value="last_30_days">30 derniers jours</option>
              <option value="previous_month">Mois dernier</option>
            </select>
          </label>
          <button
            type="button"
            class="analysis-button"
            :disabled="!runDomainUuid || operationRunning"
            @click="runManualAnalysis"
          >
            <i class="fas fa-chart-line" aria-hidden="true"></i>
            {{ operationRunning ? 'Analyse en cours…' : 'Lancer l’analyse' }}
          </button>
          <p
            v-if="operationMessage"
            class="operation-message"
            :class="{ 'operation-message--error': operationError }"
          >
            {{ operationMessage }}
          </p>
        </div>
        <p
          v-else-if="operationMessage"
          class="operation-message operation-message--standalone"
          :class="{ 'operation-message--error': operationError }"
        >
          {{ operationMessage }}
        </p>

        <p v-if="latestPeriod && hiddenSummary" class="hidden-note">
          <i class="fas fa-eye-slash" aria-hidden="true"></i>
          <span>{{ hiddenSummary }} ·</span>
          <button type="button" class="link-button" @click="openHistory(latestPeriod.key)">
            voir dans l’historique
          </button>
        </p>

        <EmployeeConcernTable
          v-if="latestPeriod"
          :rows="latestPeriod.retainedRows"
          :operation-running="operationRunning"
          empty-label="Aucune proposition retenue sur cette période."
          @deepen="deepenConcern"
        />
        <p v-else class="concerns-state">
          La première analyse mensuelle apparaîtra ici. Vous pouvez aussi lancer une analyse sur un domaine.
        </p>
      </section>

      <b-modal
        :active.sync="historyOpen"
        :width="1180"
        :can-cancel="['escape', 'outside']"
        custom-class="dashboard-modal flex items-center justify-center py-6 lg:py-10"
        custom-content-class="w-full mx-4 flex"
        trap-focus
      >
        <EmployeeConcernHistoryModal
          v-if="historyOpen"
          :periods="periods"
          :domains="availableDomains"
          :initial-period-key="historyPeriodKey"
          :operation-running="operationRunning"
          :operation-message="operationMessage"
          :operation-error="operationError"
          @close="historyOpen = false"
          @deepen="deepenConcern"
        />
      </b-modal>
    </PageContent>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { http } from '@/services/api.service'
import PageHeader from '@/components/PageHeader.vue'
import PageContent from '@/components/PageContent.vue'
import EmployeeConcernTable from '@/components/dashboard/EmployeeConcernTable.vue'
import EmployeeConcernHistoryModal from '@/components/dashboard/EmployeeConcernHistoryModal.vue'

const dateFormatter = new Intl.DateTimeFormat('fr-FR')
const monthFormatter = new Intl.DateTimeFormat('fr-FR', { month: 'long', year: 'numeric' })

function toDate (value) {
  return new Date(`${String(value).slice(0, 10)}T12:00:00`)
}

export default {
  name: 'app-dashboard-employee-concerns',
  components: { PageHeader, PageContent, EmployeeConcernTable, EmployeeConcernHistoryModal },
  data () {
    return {
      analysisRuns: [],
      availableDomains: [],
      loading: false,
      error: '',
      manualRunEnabled: true,
      runPanelOpen: false,
      runDomainUuid: '',
      periodType: 'last_30_days',
      historyOpen: false,
      historyPeriodKey: '',
      operationRunning: false,
      operationRunId: '',
      operationMessage: '',
      operationError: false,
      operationPollAttempts: 0,
      operationPollTimer: null
    }
  },
  computed: {
    ...mapGetters(['roles']),
    hasAccess () {
      return this.roles.some(role => ['admin', 'community-manager'].includes(role))
    },
    /**
     * Analyses grouped by analysed period, most recent period first. Each period
     * carries every row (one per proposal, or one per run without proposal) and
     * the retained proposals shown on the main card.
     */
    periods () {
      const groups = new Map()
      this.analysisRuns.forEach(run => {
        if (!run.period_start || !run.period_end) return
        const key = `${run.period_start}|${run.period_end}`
        if (!groups.has(key)) {
          groups.set(key, {
            key,
            start: run.period_start,
            end: run.period_end,
            label: this.periodLabel(run.period_start, run.period_end),
            rangeLabel: `${this.formatDate(run.period_start)} – ${this.formatDate(run.period_end)}`,
            hasScheduledRun: false,
            rows: [],
            retainedRows: [],
            rejectedCount: 0,
            emptyRunCount: 0
          })
        }
        const group = groups.get(key)
        if (run.trigger === 'scheduled') group.hasScheduledRun = true
        this.runRows(run).forEach(row => {
          group.rows.push(row)
          if (!row.concern) {
            if (run.status === 'completed') group.emptyRunCount += 1
            return
          }
          if (row.concern.status === 'proposed') {
            group.retainedRows.push(row)
          } else {
            group.rejectedCount += 1
          }
        })
      })

      return [...groups.values()].sort((a, b) => {
        if (a.end !== b.end) return a.end < b.end ? 1 : -1
        return a.start < b.start ? 1 : -1
      })
    },
    /** The last monthly analysis; before any monthly run exists, the last period with proposals. */
    latestPeriod () {
      return this.periods.find(period => period.hasScheduledRun) ||
        this.periods.find(period => period.rows.some(row => row.concern)) ||
        this.periods[0] ||
        null
    },
    hiddenSummary () {
      if (!this.latestPeriod) return ''
      const parts = []
      const rejected = this.latestPeriod.rejectedCount
      if (rejected > 0) {
        parts.push(`${rejected} proposition${rejected > 1 ? 's' : ''} rejetée${rejected > 1 ? 's' : ''} masquée${rejected > 1 ? 's' : ''}`)
      }
      const empty = this.latestPeriod.emptyRunCount
      if (empty > 0) {
        parts.push(`${empty} domaine${empty > 1 ? 's' : ''} sans sujet exploitable`)
      }
      return parts.join(' · ')
    }
  },
  async created () {
    if (!Array.isArray(this.roles) || this.roles.length === 0) {
      await this.$store.dispatch('FETCH_ROLES')
    }
    if (!this.hasAccess) {
      this.$router.replace('/app/dashboard')
      return
    }
    this.loadAnalyses()
  },
  beforeDestroy () {
    if (this.operationPollTimer) window.clearTimeout(this.operationPollTimer)
  },
  methods: {
    async loadAnalyses ({ silent = false } = {}) {
      if (!silent) {
        this.loading = true
        this.error = ''
      }
      try {
        const { data } = await http.get('/dashboard/employee-concerns', { params: { limit: 500 } })
        this.analysisRuns = Array.isArray(data.analysis_runs) ? data.analysis_runs : []
        this.availableDomains = Array.isArray(data.available_domains) ? data.available_domains : []
        this.manualRunEnabled = data.manual_run_enabled === true
      } catch (err) {
        console.log(err)
        if (!silent) this.error = err?.response?.data?.message || 'Impossible de charger les analyses.'
      } finally {
        if (!silent) this.loading = false
      }
    },
    runRows (run) {
      if (!Array.isArray(run.concerns) || run.concerns.length === 0) {
        return [{ key: `${run.uuid}-empty`, run, concern: null }]
      }
      return run.concerns.map(concern => ({ key: `${run.uuid}-${concern.uuid}`, run, concern }))
    },
    openHistory (periodKey = '') {
      this.historyPeriodKey = periodKey || (this.latestPeriod ? this.latestPeriod.key : '')
      this.historyOpen = true
    },
    async runManualAnalysis () {
      const domain = this.availableDomains.find(item => item.uuid === this.runDomainUuid)
      if (!domain) return
      const period = this.periodType === 'last_30_days' ? 'les 30 derniers jours' : 'le mois dernier'
      if (!window.confirm(`Lancer l’analyse de ${period} pour « ${domain.label} » ?`)) return

      await this.startOperation(
        () => http.post('/dashboard/employee-concerns/runs', {
          domainUuid: domain.uuid,
          periodType: this.periodType
        }),
        'Ajout de l’analyse à la file d’attente…'
      )
    },
    async deepenConcern (concern) {
      if (!window.confirm('Approfondir ce sujet et ajouter une nouvelle proposition éditoriale ?')) return
      await this.startOperation(
        () => http.post(`/dashboard/employee-concerns/${concern.uuid}/deepen`),
        'Ajout de l’approfondissement à la file d’attente…'
      )
    },
    async startOperation (request, pendingMessage) {
      this.operationRunning = true
      this.operationError = false
      this.operationMessage = pendingMessage
      this.operationPollAttempts = 0
      try {
        const { data } = await request()
        this.operationRunId = data.run.id
        this.operationMessage = data.run.message
        this.scheduleOperationPoll(1500)
      } catch (err) {
        console.log(err)
        this.operationRunning = false
        this.operationError = true
        this.operationMessage = err?.response?.data?.message || 'Impossible de lancer cette opération.'
      }
    },
    scheduleOperationPoll (delay = 3000) {
      if (this.operationPollTimer) window.clearTimeout(this.operationPollTimer)
      this.operationPollTimer = window.setTimeout(() => this.pollOperation(), delay)
    },
    async pollOperation () {
      if (!this.operationRunId) return
      this.operationPollAttempts += 1
      try {
        const { data } = await http.get(`/dashboard/employee-concerns/runs/${this.operationRunId}`)
        const run = data.run || {}
        this.operationMessage = run.message || 'Analyse en cours…'
        if (['queued', 'running'].includes(run.status)) {
          if (this.operationPollAttempts >= 120) {
            this.operationRunning = false
            this.operationError = true
            this.operationMessage = 'L’analyse prend plus de temps que prévu. Revenez dans quelques minutes.'
            return
          }
          this.scheduleOperationPoll()
          return
        }

        this.operationRunning = false
        this.operationError = run.status !== 'completed'
        if (run.status === 'completed') await this.loadAnalyses({ silent: true })
      } catch (err) {
        console.log(err)
        this.operationRunning = false
        this.operationError = true
        this.operationMessage = err?.response?.data?.message || 'Impossible de suivre cette opération.'
      }
    },
    /** "Septembre 2026" for a full calendar month, the date range otherwise. */
    periodLabel (start, end) {
      const startDate = toDate(start)
      const endDate = toDate(end)
      const lastDayOfMonth = new Date(endDate.getFullYear(), endDate.getMonth() + 1, 0).getDate()
      const isFullMonth = startDate.getDate() === 1 &&
        endDate.getDate() === lastDayOfMonth &&
        startDate.getMonth() === endDate.getMonth() &&
        startDate.getFullYear() === endDate.getFullYear()
      if (!isFullMonth) return `${this.formatDate(start)} – ${this.formatDate(end)}`
      const label = monthFormatter.format(startDate)
      return label.charAt(0).toUpperCase() + label.slice(1)
    },
    formatDate (date) {
      if (!date) return '—'
      return dateFormatter.format(toDate(date))
    }
  }
}
</script>

<style scoped>
.concerns-state {
  padding: 3rem 1rem;
  color: #4b5563;
  text-align: center;
}

.concerns-state--error {
  color: #b91c1c;
}

.results-card {
  overflow: hidden;
  border: 1px solid #bfdbfe;
  border-radius: 0.8rem;
  background: #fff;
}

.results-header {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  padding: 1.25rem 1.5rem;
}

.results-heading h2 {
  margin: 0;
  color: #111827;
  font-size: 1.125rem;
  font-weight: 600;
  line-height: 1.3;
}

.results-heading p {
  margin: 0.25rem 0 0;
  color: #4b5563;
  font-size: 0.8rem;
}

.results-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.6rem;
}

.outline-button {
  display: inline-flex;
  min-height: 2.5rem;
  align-items: center;
  gap: 0.5rem;
  border: 1px solid #e5e7eb;
  border-radius: 0.55rem;
  background: #fff;
  padding: 0 1rem;
  color: #111827;
  font-size: 0.875rem;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease;
}

.outline-button:hover,
.outline-button--active {
  border-color: #bfdbfe;
  background: #f9fafb;
}

.outline-button--primary {
  border-color: #2563eb;
  color: #2563eb;
  font-weight: 600;
}

.outline-button--primary:hover {
  background: #eff6ff;
}

.outline-button:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

.run-panel {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 1rem;
  margin: 0 1.5rem 1.25rem;
  border: 1px solid #bfdbfe;
  border-radius: 0.65rem;
  background: #f8fbff;
  padding: 1rem 1.15rem;
}

.run-field {
  display: flex;
  min-width: 13rem;
  flex-direction: column;
  gap: 0.35rem;
  color: #111827;
  font-size: 0.875rem;
}

.run-field--domain {
  min-width: 20rem;
  flex: 1;
}

.run-field span {
  color: #4b5563;
  font-size: 0.75rem;
  font-weight: 700;
}

.run-field select {
  height: 2.65rem;
  border: 1px solid #e5e7eb;
  border-radius: 0.55rem;
  background: #fff;
  padding: 0 0.75rem;
  color: #111827;
}

.analysis-button {
  display: inline-flex;
  min-height: 2.65rem;
  align-items: center;
  justify-content: center;
  gap: 0.45rem;
  border: 0;
  border-radius: 0.55rem;
  background: #2563eb;
  padding: 0 1.1rem;
  color: #fff;
  cursor: pointer;
}

.analysis-button:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

.operation-message {
  flex-basis: 100%;
  margin: 0;
  color: #2563eb;
  font-size: 0.75rem;
}

.operation-message--standalone {
  margin: 0 1.5rem 1rem;
}

.operation-message--error {
  color: #b91c1c;
}

.hidden-note {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.4rem;
  margin: 0;
  border-top: 1px solid #e5e7eb;
  background: #f9fafb;
  padding: 0.6rem 1.5rem;
  color: #6b7280;
  font-size: 0.8rem;
}

.hidden-note i {
  color: #9ca3af;
}

.link-button {
  border: 0;
  background: none;
  padding: 0;
  color: #2563eb;
  font: inherit;
  cursor: pointer;
}

.link-button:hover {
  text-decoration: underline;
}

@media (max-width: 1100px) {
  .run-field--domain {
    min-width: 16rem;
  }
}
</style>
